import React, { FC, useCallback, useMemo } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { UseQueryResult } from '@tanstack/react-query';
import { PopUpProductDetails, ProductsSequenceProduct } from 'common/contracts';
import { ProductEntry } from 'views/Offers/Popups/Form/hooks/usePopupProducts';

import Input from 'components/Input/Input';
import InputSelectDropdown, {
  InputSelectDropdownOption
} from 'components/InputSelectDropdown/InputSelectDropdown';

const DEFAULT_QUANTITY = 0;

interface IDayProductsListProps {
  productsList: ProductEntry[];
  products: UseQueryResult;
  addProduct: (product: ProductsSequenceProduct) => void;
  updateProduct: (id: string, product: ProductsSequenceProduct) => void;
  deleteProduct: (id: string) => void;
}

export const DayProductsList: FC<IDayProductsListProps> = ({
  productsList,
  products,
  addProduct,
  updateProduct,
  deleteProduct
}) => {
  const productsData = products.data as
    | { result: PopUpProductDetails[] }
    | undefined;

  const allProductOptions = useMemo<InputSelectDropdownOption[]>(() => {
    if (!productsData) return [];
    return productsData.result.map((product) => ({
      label: product.displayName || product.name || '',
      value: product.productId || '',
      image: product.images?.[0]?.url
    }));
  }, [productsData]);

  const selectedProductIds = useMemo(
    () => new Set(productsList.map((e) => e.productId).filter(Boolean)),
    [productsList]
  );

  const getOptionsForEntry = useCallback(
    (entryId: string): InputSelectDropdownOption[] => {
      const currentEntryProductId = productsList.find((e) => e.id === entryId)?.productId;
      return allProductOptions.filter(
        (opt) =>
          opt.value === currentEntryProductId ||
          !selectedProductIds.has(String(opt.value))
      );
    },
    [allProductOptions, selectedProductIds, productsList]
  );

  const findProduct = useCallback(
    (productId: string): PopUpProductDetails | undefined =>
      productsData?.result.find((p) => p.productId === productId),
    [productsData]
  );

  const buildRowFromProduct = useCallback(
    (
      product: PopUpProductDetails,
      quantity: number
    ): ProductsSequenceProduct => ({
      productId: product.productId || '',
      publisherProductId: product.publisherProductId || '',
      quantity,
      displayName: product.displayName || product.name || '',
      image: product.images?.[0]?.url || ''
    } as ProductsSequenceProduct),
    []
  );

  const handleProductChange = (entry: ProductEntry, newProductId: string): void => {
    const selected = findProduct(newProductId);
    if (!selected) return;
    const row = buildRowFromProduct(selected, Number(entry.quantity) || DEFAULT_QUANTITY);
    updateProduct(entry.id, { ...row, priority: entry.priority });
  };

  const handleQuantityChange = (entry: ProductEntry, nextQuantity: number): void => {
    updateProduct(entry.id, { ...entry, quantity: Math.max(0, nextQuantity) });
  };

  const canDeleteRow = productsList.length > 1;

  return (
    <div className="flex flex-col gap-2 w-full">
      {productsList.map((entry) => (
        <div
          key={entry.id}
          className="box-border flex w-full min-h-[38px] min-w-0 items-center gap-2 rounded-md bg-[#FAFAFA] px-[7px] py-2"
        >
          <div className="flex min-h-0 min-w-0 flex-1 items-center">
            <InputSelectDropdown
              placeholder="Choose"
              options={getOptionsForEntry(entry.id)}
              defaultSelected={allProductOptions.find(
                (option) => option.value === entry.productId
              )}
              onOptionChange={(value) => handleProductChange(entry, value)}
              buttonClassName="min-h-0 w-full min-w-0 px-3 py-1.5"
              className="min-w-0 w-full"
              dropdownSide="bottom"
              dropdownAvoidCollisions={false}
            />
          </div>
          <Input
            name={`quantity-${entry.id}`}
            type="number"
            min={0}
            className="w-[68px] shrink-0"
            inputClassName="box-border min-h-0 px-2 py-1.5"
            value={entry.quantity ?? DEFAULT_QUANTITY}
            onChange={(e) =>
              handleQuantityChange(entry, Number(e.target.value))
            }
          />
          {canDeleteRow && (
            <button
              type="button"
              aria-label="Delete reward"
              className="box-border flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-base-mutedForeground hover:bg-base-accent hover:text-base-foreground"
              onClick={() => deleteProduct(entry.id)}
            >
              <FontAwesomeIcon icon="fa-regular fa-trash-can" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
};
